import camelcaseKeys from 'camelcase-keys';
import DealAPI from 'dashboard/api/deals';
import { createStore } from 'dashboard/store/storeFactory';
import { throwErrorMessage } from 'dashboard/store/utils/api';
import snakecaseKeys from 'snakecase-keys';

const camelizeDeal = data =>
  camelcaseKeys(data || {}, {
    deep: true,
    stopPaths: ['custom_attributes', 'additional_attributes'],
  });

const normalizeMeta = meta => ({
  ...camelcaseKeys(meta || {}),
  totalCount: Number(meta?.total_count || meta?.totalCount || meta?.count || 0),
  page: Number(meta?.page || meta?.current_page || 1),
});

const buildDealPayload = ({
  customAttributes,
  additionalAttributes,
  ...rest
}) => {
  const payload = {
    ...snakecaseKeys(rest, { deep: true }),
    ...(customAttributes && { custom_attributes: customAttributes }),
    ...(additionalAttributes && {
      additional_attributes: additionalAttributes,
    }),
  };
  return { deal: payload };
};

export const useDealsStore = createStore({
  name: 'deals',
  type: 'pinia',
  API: DealAPI,
  state: () => ({
    activeDealId: null,
    kanbanData: [],
    searchResults: [],
    searchMeta: {},
    dealDetailRequestToken: 0,
  }),

  getters: {
    getDealsList: state => state.records,
    getActiveDeal: state =>
      state.records.find(d => d.id === state.activeDealId) || {},
    getKanbanData: state => state.kanbanData,
    getPendingActivities: state =>
      state.records.filter(a => a.status === 'pending'),
  },

  actions: () => ({
    setMeta(meta) {
      this.meta = normalizeMeta(meta);
    },

    setActiveDealId(dealId) {
      this.activeDealId = Number(dealId);
    },

    upsertDealRecord(record) {
      const index = this.records.findIndex(r => r.id === record.id);
      if (index === -1) this.records.push(record);
      else this.records[index] = record;
    },

    async get(params = {}) {
      this.setUIFlag({ fetchingList: true });
      try {
        const response = await DealAPI.get(params);
        const deals = (response.data.payload || []).map(d => camelizeDeal(d));
        this.records = deals;
        if (response.data.meta) this.setMeta(response.data.meta);
        return deals;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async show(id) {
      this.setUIFlag({ fetchingItem: true });
      this.setActiveDealId(id);
      const requestToken = (this.dealDetailRequestToken || 0) + 1;
      this.dealDetailRequestToken = requestToken;
      try {
        const response = await DealAPI.show(id);
        const deal = camelizeDeal(response.data.payload);
        if (this.dealDetailRequestToken !== requestToken) return deal;
        this.upsertDealRecord(deal);
        this.setActiveDealId(deal.id);
        return deal;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        if (this.dealDetailRequestToken === requestToken) {
          this.setUIFlag({ fetchingItem: false });
        }
      }
    },

    async create(dealAttrs) {
      this.setUIFlag({ creatingItem: true });
      try {
        const response = await DealAPI.create(buildDealPayload(dealAttrs));
        const deal = camelizeDeal(response.data.payload);
        this.upsertDealRecord(deal);
        return deal;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ creatingItem: false });
      }
    },

    async update({ id, ...dealAttrs }) {
      this.setUIFlag({ updatingItem: true });
      try {
        const response = await DealAPI.update(id, buildDealPayload(dealAttrs));
        const deal = camelizeDeal(response.data.payload);
        this.upsertDealRecord(deal);
        return deal;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ updatingItem: false });
      }
    },

    async delete(id) {
      this.setUIFlag({ deletingItem: true });
      try {
        await DealAPI.delete(id);
        this.records = this.records.filter(r => r.id !== Number(id));
        return Number(id);
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ deletingItem: false });
      }
    },

    async fetchKanban(pipelineId) {
      this.setUIFlag({ fetchingKanban: true });
      try {
        const response = await DealAPI.kanban(pipelineId);
        const kanban = (response.data.payload || []).map(column => ({
          stage: camelizeDeal(column.stage),
          deals: (column.deals || []).map(d => camelizeDeal(d)),
        }));
        this.kanbanData = kanban;
        return kanban;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingKanban: false });
      }
    },

    async moveStage(dealId, stageId) {
      this.setUIFlag({ movingStage: true });
      try {
        const response = await DealAPI.moveStage(dealId, stageId);
        const updatedDeal = camelizeDeal(response.data.payload);
        this.upsertDealRecord(updatedDeal);
        // Update kanban data if loaded
        this.kanbanData = this.kanbanData.map(column => {
          const dealIndex = column.deals.findIndex(
            d => d.id === Number(dealId)
          );
          if (dealIndex === -1) return column;
          return {
            ...column,
            deals: column.deals.filter(d => d.id !== Number(dealId)),
          };
        });
        // Add deal to the new stage column
        const newStageId = Number(stageId);
        this.kanbanData = this.kanbanData.map(column => {
          if (column.stage.id !== newStageId) return column;
          return {
            ...column,
            deals: [...column.deals, updatedDeal],
          };
        });
        return updatedDeal;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ movingStage: false });
      }
    },

    async search(query, page = 1) {
      this.setUIFlag({ searching: true });
      try {
        const response = await DealAPI.search(query, page);
        const deals = (response.data.payload || []).map(d => camelizeDeal(d));
        this.searchResults = deals;
        if (response.data.meta)
          this.searchMeta = normalizeMeta(response.data.meta);
        return deals;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ searching: false });
      }
    },
  }),
});
